import React from 'react'
import './CookiesPolicy.css'

const CookiesPolicy = () => {
  return (
    <div className="cookies-policy">
      <div className="cookies-header">
        <h1>NepliShop Cookies Policy</h1>
        <p className="effective-date">Last Updated: January 2024</p>
        <p className="policy-intro">
          This Cookies Policy explains how NepliShop uses cookies and similar technologies on our website neplishop.com. By using our website, you consent to the use of cookies as described in this policy.
        </p>
      </div>

      <div className="cookies-content">
        <section className="policy-section">
          <h2>1. What Are Cookies?</h2>
          <div className="section-content">
            <p>Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They help websites remember your preferences and provide a better browsing experience.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>2. Types of Cookies We Use</h2>
          <div className="section-content">
            <h3>Essential Cookies</h3>
            <ul>
              <li><strong>Purpose:</strong> Necessary for basic website functionality</li>
              <li><strong>Examples:</strong> Session management, shopping cart, login authentication</li>
              <li><strong>Duration:</strong> Session or persistent</li>
            </ul>

            <h3>Performance Cookies</h3>
            <ul>
              <li><strong>Purpose:</strong> Collect anonymous information about website usage</li>
              <li><strong>Examples:</strong> Google Analytics, page visits, bounce rates</li>
              <li><strong>Duration:</strong> Persistent (up to 2 years)</li>
            </ul>

            <h3>Functional Cookies</h3>
            <ul>
              <li><strong>Purpose:</strong> Remember your preferences and settings</li>
              <li><strong>Examples:</strong> Language selection, font size, region settings</li>
              <li><strong>Duration:</strong> Persistent (up to 1 year)</li>
            </ul>

            <h3>Targeting/Advertising Cookies</h3>
            <ul>
              <li><strong>Purpose:</strong> Deliver relevant advertisements</li>
              <li><strong>Examples:</strong> Facebook Pixel, Google Ads, retargeting</li>
              <li><strong>Duration:</strong> Persistent (up to 1 year)</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>3. Specific Cookies We Use</h2>
          <div className="section-content">
            <div className="cookies-table">
              <table>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Provider</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>nepli_session</td>
                    <td>User session management</td>
                    <td>Session</td>
                    <td>NepliShop</td>
                  </tr>
                  <tr>
                    <td>cart_items</td>
                    <td>Shopping cart functionality</td>
                    <td>30 days</td>
                    <td>NepliShop</td>
                  </tr>
                  <tr>
                    <td>language_pref</td>
                    <td>Language preference</td>
                    <td>1 year</td>
                    <td>NepliShop</td>
                  </tr>
                  <tr>
                    <td>_ga</td>
                    <td>Google Analytics tracking</td>
                    <td>2 years</td>
                    <td>Google</td>
                  </tr>
                  <tr>
                    <td>_fbp</td>
                    <td>Facebook Pixel conversion tracking</td>
                    <td>3 months</td>
                    <td>Facebook</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="policy-section">
          <h2>4. Why We Use Cookies</h2>
          <div className="section-content">
            <ul>
              <li><strong>Essential Functionality:</strong> Enable shopping cart, checkout process, and user authentication</li>
              <li><strong>Personalization:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics:</strong> Understand how users interact with our website to improve performance</li>
              <li><strong>Marketing:</strong> Show relevant products and offers based on your interests</li>
              <li><strong>Security:</strong> Protect against fraud and ensure secure transactions</li>
              <li><strong>Performance:</strong> Optimize website speed and responsiveness</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>5. Third-Party Cookies</h2>
          <div className="section-content">
            <p>We work with trusted third parties that may set cookies on our website:</p>
            <ul>
              <li><strong>Google Analytics:</strong> For website analytics and performance tracking</li>
              <li><strong>Facebook Pixel:</strong> For advertising and conversion tracking</li>
              <li><strong>Payment Processors:</strong> eSewa, Khalti, ConnectIPS for secure transactions</li>
              <li><strong>Social Media:</strong> Facebook, Instagram, Pinterest for social sharing</li>
            </ul>
            <p>These third parties have their own privacy policies and cookie practices.</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>6. Managing Cookie Preferences</h2>
          <div className="section-content">
            <h3>Browser Settings</h3>
            <p>You can control cookies through your browser settings:</p>
            <ul>
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
              <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
            </ul>

            <h3>Cookie Consent Banner</h3>
            <p>When you first visit our website, you can choose which types of cookies to accept through our cookie consent banner.</p>

            <h3>Opt-Out Tools</h3>
            <ul>
              <li><strong>Google Analytics:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
              <li><strong>Digital Advertising Alliance:</strong> <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">YourAdChoices</a></li>
              <li><strong>Network Advertising Initiative:</strong> <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">NAI Opt-out</a></li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>7. Consequences of Disabling Cookies</h2>
          <div className="section-content">
            <p>If you disable cookies, some features of our website may not function properly:</p>
            <ul>
              <li>Shopping cart and checkout may not work</li>
              <li>You may need to re-enter information multiple times</li>
              <li>Personalized recommendations won't be available</li>
              <li>Some website features may be limited or unavailable</li>
              <li>You may see irrelevant advertisements</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>8. Legal Compliance</h2>
          <div className="section-content">
            <p>Our use of cookies complies with:</p>
            <ul>
              <li>Electronic Transactions Act, 2063 (2008) of Nepal</li>
              <li>Individual Privacy Act, 2075 (2018) of Nepal</li>
              <li>General Data Protection Regulation (GDPR) for international users</li>
              <li>California Consumer Privacy Act (CCPA) for California residents</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>9. Updates to This Policy</h2>
          <div className="section-content">
            <p>We may update this Cookies Policy periodically. We will notify you of significant changes by:</p>
            <ul>
              <li>Updating the "Last Updated" date at the top of this page</li>
              <li>Displaying a notice on our website</li>
              <li>Seeking renewed consent when required by law</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>10. Contact Information</h2>
          <div className="section-content contact-details">
            <p><strong>NepliShop Pvt. Ltd.</strong></p>
            <p>Kathmandu, Nepal</p>
            <p>Email: neplishop.com@gmail.com</p>
            <p>Phone: +977 9741760149</p>
            <p>Website: www.neplishop.com</p>
            
            <div className="regulatory-info">
              <h3>Data Protection Officer</h3>
              <p>For questions about cookies or data protection, contact:</p>
              <p>Mr. Dikendra Ray, MD Mahatab Miya, Nishant Shrestha</p>
              <p>Email: neplishop.com@gmail.com</p>
              <p>Phone: +977 9741761409</p>
            </div>
          </div>
        </section>

        <div className="acceptance-section">
          <p className="acceptance-note">
            <strong>By continuing to use our website, you acknowledge that you have read and understood this Cookies Policy.</strong>
          </p>
          <p className="nepali-translation">
            <strong>नेपाली अनुवाद:</strong> हाम्रो वेबसाइट प्रयोग गर्दै जानुहुँदा तपाईंले यो कुकीज नीति पढेर बुझेको र स्वीकार गर्नुभएको बुझिनेछ।
          </p>
        </div>
      </div>
    </div>
  )
}

export default CookiesPolicy
